"use client";

import {
  z_ChatV8Event,
  z_ChatV8Run,
  type T_ChatV8ElicitationAnswer,
  type T_ChatV8RunMode,
} from "@lattice/core-lib/types/chat-v8";
import {
  canUseChatV8Composer,
  chatV8ClientMachine,
  chatV8CoordinatorMachine,
  getChatV8ClientViewState,
  isThreadNotFoundError,
  isTimelineChangedError,
  materializeChatV8RenderableItems,
  type T_ChatV8ClientEvent,
  type T_ChatV8CoordinatorObservation,
  type T_ChatV8RenderableItem,
  type T_ChatV8UploadedAttachment,
} from "@lattice/shared-frontend/chat-v8";
import { getUsageLimitErrorMessage } from "@lattice/shared-frontend/usage";
import { useActorRef, useSelector } from "@xstate/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  isClientTraceActive,
  logClientTrace,
} from "@lattice/frontend/lib/observability/client-trace";
import { getChatV8EventTraceAttributes } from "./chat-v8-client-trace";
import { createWebChatV8Transport } from "./chat-v8-transport";

export type T_UseChatV8Args = {
  mode?: T_ChatV8RunMode;
  onThreadListChanged?: () => void;
  routeThreadCuid: string | null;
  updateRouteThreadCuid: (threadCuid: string | null) => void;
};

export type T_UseChatV8Result = {
  canUseComposer: boolean;
  handleAnswerElicitation: (
    item: T_ChatV8RenderableItem,
    answer: T_ChatV8ElicitationAnswer,
  ) => Promise<void>;
  handleCancelRun: () => Promise<void>;
  handleLoadOlder: () => Promise<void>;
  handleSendMessage: (
    message: string,
    options?: { attachments?: T_ChatV8UploadedAttachment[] },
  ) => Promise<void>;
  loadOlderPending: boolean;
  renderableItems: T_ChatV8RenderableItem[];
  state: ReturnType<typeof getChatV8ClientViewState>;
  transport: ReturnType<typeof createWebChatV8Transport>;
};

type T_ChatV8WebCallbacks = Pick<
  T_UseChatV8Args,
  "onThreadListChanged" | "updateRouteThreadCuid"
>;

function messageForError(error: unknown): string {
  return (
    getUsageLimitErrorMessage(error) ??
    (error instanceof Error
      ? error.message
      : "Prism could not complete that request.")
  );
}

function subscribeToAppForeground(listener: () => void): () => void {
  if (typeof document === "undefined") return () => undefined;
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") listener();
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);
  return () =>
    document.removeEventListener("visibilitychange", handleVisibilityChange);
}

function subscribeToNetworkRecovery(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("online", listener);
  return () => window.removeEventListener("online", listener);
}

function traceCoordinatorObservation(
  observation: T_ChatV8CoordinatorObservation,
): void {
  switch (observation.kind) {
    case "capabilities_load_failed":
      logClientTrace({
        event: "capabilities.load.failed",
        scope: "chat-v8.transport",
        attributes: {
          error_type:
            observation.error instanceof Error
              ? observation.error.name
              : typeof observation.error,
        },
      });
      return;
    case "thread_load_started":
      logClientTrace({
        event: "thread.tail.load.started",
        scope: "chat-v8.transport",
      });
      return;
    case "thread_load_completed":
      logClientTrace({
        event: "thread.tail.load.completed",
        scope: "chat-v8.transport",
        attributes: {
          active_run: observation.page.activeRun !== null,
          event_count: observation.page.events.length,
          latest_seq: observation.page.latestSeq,
          timeline_version: observation.page.timelineVersion,
        },
      });
      return;
    case "thread_load_failed":
      logClientTrace({
        event: "thread.tail.load.failed",
        scope: "chat-v8.transport",
        attributes: {
          error_type:
            observation.error instanceof Error
              ? observation.error.name
              : typeof observation.error,
        },
      });
      return;
    case "subscription_event_rejected":
      logClientTrace({
        event: "subscription.event.rejected",
        scope: "chat-v8.transport",
      });
      return;
    case "subscription_event_received":
      if (!isClientTraceActive()) return;
      logClientTrace({
        event: "subscription.event.received",
        scope: "chat-v8.transport",
        attributes: getChatV8EventTraceAttributes(observation.event),
      });
      return;
    case "subscription_failed":
      logClientTrace({
        event: "subscription.failed",
        scope: "chat-v8.transport",
        attributes: {
          error_type:
            observation.error instanceof Error
              ? observation.error.name
              : typeof observation.error,
        },
      });
      return;
    case "subscription_reconnect_scheduled":
      logClientTrace({
        event: "subscription.reconnect.scheduled",
        scope: "chat-v8.transport",
        attributes: {
          reason: observation.reason,
          retry_delay_ms: observation.delayMs,
        },
      });
      return;
    case "subscription_started":
      logClientTrace({
        event: "subscription.started",
        scope: "chat-v8.transport",
        attributes: {
          after_seq: observation.afterSeq,
          timeline_version: observation.timelineVersion,
        },
      });
  }
}

/** Web adapter for the shared Chat V8 client and coordinator machines. */
export function useChatV8({
  mode,
  onThreadListChanged,
  routeThreadCuid,
  updateRouteThreadCuid,
}: T_UseChatV8Args): T_UseChatV8Result {
  const transport = useMemo(createWebChatV8Transport, []);
  const actor = useActorRef(chatV8ClientMachine, { input: undefined });
  const snapshot = useSelector(actor, (current) => current);
  const state = getChatV8ClientViewState(snapshot);
  const callbacksRef = useRef<T_ChatV8WebCallbacks>({
    onThreadListChanged,
    updateRouteThreadCuid,
  });
  callbacksRef.current = { onThreadListChanged, updateRouteThreadCuid };

  const dispatch = useCallback(
    (event: T_ChatV8ClientEvent) => actor.send(event),
    [actor],
  );
  const getCurrentState = useCallback(
    () => actor.getSnapshot().context,
    [actor],
  );
  const coordinatorActor = useActorRef(chatV8CoordinatorMachine, {
    input: {
      dispatchClient: dispatch,
      formatError: messageForError,
      getClientState: getCurrentState,
      initialThreadCuid: routeThreadCuid,
      onObservation: traceCoordinatorObservation,
      onThreadMissing: () => {
        callbacksRef.current.updateRouteThreadCuid(null);
      },
      refreshThreadSelector: () => {
        callbacksRef.current.onThreadListChanged?.();
      },
      subscribeToAppForeground,
      subscribeToClient: (listener) => {
        const subscription = actor.subscribe(listener);
        return () => subscription.unsubscribe();
      },
      subscribeToNetworkRecovery,
      trackTerminalRunEvent: () => undefined,
      transport,
    },
  });
  const widgetSnapshotsByArtifactCuid = useSelector(
    coordinatorActor,
    (current) => current.context.widgetSnapshotsByArtifactCuid,
  );
  const loadOlderPending = useSelector(
    coordinatorActor,
    (current) => current.context.loadOlderPending,
  );
  const previousRouteThreadCuidRef = useRef(routeThreadCuid);

  // eslint-disable-next-line no-restricted-syntax -- route ownership is external to the shared coordinator.
  useEffect(() => {
    if (previousRouteThreadCuidRef.current === routeThreadCuid) return;
    previousRouteThreadCuidRef.current = routeThreadCuid;
    coordinatorActor.send({
      type: "ROUTE_CHANGED",
      threadCuid: routeThreadCuid,
    });
  }, [coordinatorActor, routeThreadCuid]);

  // eslint-disable-next-line no-restricted-syntax -- client trace snapshots synchronize actor state with external observability.
  useEffect(() => {
    logClientTrace({
      event: "state.snapshot",
      scope: "chat-v8.client",
      attributes: {
        event_count: state.events.length,
        has_thread: state.threadCuid !== null,
        latest_seq: state.latestSeq,
        status: state.status,
        timeline_version: state.timelineVersion,
      },
    });
  }, [
    state.events.length,
    state.latestSeq,
    state.status,
    state.threadCuid,
    state.timelineVersion,
  ]);

  const handleSendMessage = useCallback(
    async (
      message: string,
      options?: { attachments?: T_ChatV8UploadedAttachment[] },
    ) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      const attachments =
        options?.attachments && options.attachments.length > 0
          ? options.attachments
          : undefined;
      const current = getCurrentState();
      dispatch({ type: "REQUEST_STARTED", status: "sending" });
      logClientTrace({
        event: "message.send.started",
        scope: "chat-v8.transport",
        attributes: {
          has_thread: current.threadCuid !== null,
          message_length: trimmed.length,
          mode,
        },
      });
      try {
        const result = await transport.sendMessage({
          ...(attachments ? { attachments } : {}),
          message: trimmed,
          mode,
          threadCuid: current.threadCuid ?? undefined,
        });
        const userEvent = z_ChatV8Event.safeParse(result.userEvent);
        const run = z_ChatV8Run.safeParse(result.run);
        if (!userEvent.success || !run.success) {
          throw new Error("Invalid Chat V8 send response.");
        }
        dispatch({
          type: "SEND_OK",
          result: { ...result, run: run.data, userEvent: userEvent.data },
        });
        logClientTrace({
          event: "message.send.completed",
          scope: "chat-v8.transport",
          attributes: {
            run_started: true,
            thread_created: current.threadCuid === null,
            user_event_seq: userEvent.data.seq,
          },
        });
        callbacksRef.current.onThreadListChanged?.();
        if (current.threadCuid !== result.threadCuid) {
          callbacksRef.current.updateRouteThreadCuid(result.threadCuid);
        }
      } catch (error) {
        if (isTimelineChangedError(error) && current.threadCuid) {
          coordinatorActor.send({ type: "REFRESH_THREAD" });
          return;
        }
        if (isThreadNotFoundError(error) && current.threadCuid) {
          coordinatorActor.send({ type: "THREAD_MISSING" });
          return;
        }
        dispatch({ type: "MUTATION_ERROR", error: messageForError(error) });
        logClientTrace({
          event: "message.send.failed",
          scope: "chat-v8.transport",
          attributes: {
            error_type: error instanceof Error ? error.name : typeof error,
            has_thread: current.threadCuid !== null,
          },
        });
      }
    },
    [coordinatorActor, dispatch, getCurrentState, mode, transport],
  );

  const handleAnswerElicitation = useCallback(
    async (item: T_ChatV8RenderableItem, answer: T_ChatV8ElicitationAnswer) => {
      const threadCuid = getCurrentState().threadCuid;
      if (!threadCuid) return;
      dispatch({ type: "REQUEST_STARTED", status: "streaming" });
      try {
        await transport.answerElicitation({
          answer,
          itemCuid: item.itemCuid,
          threadCuid,
        });
        coordinatorActor.send({ type: "REFRESH_THREAD" });
        callbacksRef.current.onThreadListChanged?.();
      } catch (error) {
        if (isTimelineChangedError(error)) {
          coordinatorActor.send({ type: "REFRESH_THREAD" });
          return;
        }
        if (isThreadNotFoundError(error)) {
          coordinatorActor.send({ type: "THREAD_MISSING" });
          return;
        }
        dispatch({ type: "MUTATION_ERROR", error: messageForError(error) });
      }
    },
    [coordinatorActor, dispatch, getCurrentState, transport],
  );

  const handleLoadOlder = useCallback(async () => {
    coordinatorActor.send({ type: "LOAD_OLDER" });
  }, [coordinatorActor]);

  const handleCancelRun = useCallback(async () => {
    const current = getCurrentState();
    if (!current.activeRun) return;
    logClientTrace({
      event: "run.cancel.requested",
      scope: "chat-v8.transport",
      attributes: { has_thread: current.threadCuid !== null },
    });
    try {
      const result = await transport.cancelRun({
        runCuid: current.activeRun.cuid,
      });
      dispatch({ type: "RUN_UPDATED", run: result });
      if (current.threadCuid) {
        coordinatorActor.send({ type: "REFRESH_THREAD" });
      }
    } catch (error) {
      dispatch({ type: "MUTATION_ERROR", error: messageForError(error) });
    }
  }, [coordinatorActor, dispatch, getCurrentState, transport]);

  const renderableItems = useMemo(
    () =>
      materializeChatV8RenderableItems(state.events, {
        widgetSnapshotsByArtifactCuid,
      }),
    [state.events, widgetSnapshotsByArtifactCuid],
  );

  return {
    canUseComposer: canUseChatV8Composer(state.status),
    handleAnswerElicitation,
    handleCancelRun,
    handleLoadOlder,
    handleSendMessage,
    loadOlderPending,
    renderableItems,
    state,
    transport,
  };
}
